import { useCallback, useEffect, useState } from "react";
import { Download, LogOut, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { http, setToken, getToken, errorText } from "@/lib/api";

const Panel = ({ title, children, testid, className = "" }) => (
  <section className={`border border-hv-rule bg-white p-6 ${className}`} data-testid={testid}>
    <h2 className="border-b border-hv-rule pb-3 font-heading text-[14px] font-semibold text-hv-text">{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

const Kpi = ({ label, value, testid }) => (
  <div className="border border-hv-rule bg-white p-5">
    <p className="font-heading text-[11px] font-semibold uppercase tracking-wider text-hv-muted">{label}</p>
    <p className="mt-1 font-heading text-3xl font-bold text-hv-navy" data-testid={testid}>{value}</p>
  </div>
);

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      setToken(data.token);
      onDone(data.user);
    } catch (ex) {
      setErr(errorText(ex.response?.data?.detail) || ex.message);
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center hv-hero px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white p-8" data-testid="admin-login-form">
        <img src="/brand/etf-header.svg" alt="Harvest ETFs" className="h-8 w-auto" />
        <h1 className="mt-6 font-heading text-2xl font-bold text-hv-text">Ceres Admin</h1>
        <label className="mt-6 block font-heading text-xs font-semibold uppercase tracking-wider text-hv-muted" htmlFor="adm-email">Email</label>
        <input id="adm-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input" className="mt-1 w-full border border-hv-rule px-3 py-2.5 focus:border-hv-navy focus:outline-none" />
        <label className="mt-4 block font-heading text-xs font-semibold uppercase tracking-wider text-hv-muted" htmlFor="adm-pw">Password</label>
        <input id="adm-pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input" className="mt-1 w-full border border-hv-rule px-3 py-2.5 focus:border-hv-navy focus:outline-none" />
        {err && <p role="alert" className="mt-3 text-sm font-semibold text-hv-navy" data-testid="admin-login-error">{err}</p>}
        <button type="submit" className="hv-btn-light mt-6 w-full" data-testid="admin-login-submit-button">Sign in</button>
      </form>
    </div>
  );
};

const RankList = ({ items, label, testid }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <ol className="space-y-2" data-testid={testid}>
      {items.map((i, n) => (
        <li key={n} className="text-[13px]">
          <div className="flex justify-between gap-3"><span className="truncate text-hv-text">{i[label]}</span><span className="font-heading font-bold text-hv-navy">{i.count}</span></div>
          <div className="mt-1 h-1.5 bg-hv-section"><div className="h-full bg-hv-logo-navy" style={{ width: `${(100 * i.count) / max}%` }} /></div>
        </li>
      ))}
      {items.length === 0 && <li className="text-sm text-hv-muted">No data yet.</li>}
    </ol>
  );
};

const Transcripts = ({ items }) => (
  <Accordion type="multiple" data-testid="thumbs-down-transcripts">
    {items.map((x) => (
      <AccordionItem key={x.id} value={x.id}>
        <AccordionTrigger className="text-left text-[13px] font-semibold text-hv-text">{x.question} <span className="ml-2 font-normal text-hv-muted">{x.created_at.slice(0, 10)} · {x.lang.toUpperCase()}</span></AccordionTrigger>
        <AccordionContent>
          <div className="space-y-2">
            {x.messages.map((m, i) => (
              <p key={i} className={`whitespace-pre-wrap p-2 text-[12px] ${m.role === "user" ? "bg-hv-navy text-white" : "bg-hv-section text-hv-text"}`}><strong>{m.role === "user" ? "User" : "Ceres"}:</strong> {m.content}</p>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    ))}
    {items.length === 0 && <p className="text-sm text-hv-muted">No thumbs-down answers in this period.</p>}
  </Accordion>
);

const Dashboard = ({ user, onLogout }) => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const load = useCallback(async () => {
    const [a, s] = await Promise.all([http.get(`/admin/analytics?days=${days}`), http.get("/status")]);
    setData(a.data);
    setStatus(s.data);
  }, [days]);
  useEffect(() => { load().catch((e) => e.response?.status === 401 && onLogout()); }, [load, onLogout]);

  const exportCsv = async () => {
    const r = await http.get(`/admin/export.csv?days=${days}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ceres_chat_logs_${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const crawl = async () => {
    const r = await http.post("/admin/crawl", { mode: "prices" });
    toast(r.data.started ? "Price refresh started from harvestportfolios.com" : "A crawl is already running");
  };

  return (
    <div className="min-h-screen bg-hv-section" data-testid="admin-dashboard">
      <header className="border-b border-hv-rule bg-white">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3"><img src="/brand/etf-header.svg" alt="Harvest ETFs" className="h-7" /><span className="font-heading font-bold text-hv-navy">Ceres Analytics</span></div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-hv-muted sm:inline">{user.email}</span>
            <button type="button" onClick={onLogout} className="hv-btn-light hv-btn-sm" data-testid="admin-logout-button"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1280px] space-y-6 px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-hv-text">Chat analytics</h1>
            {data?.sample_count > 0 && <p className="mt-1 text-sm text-hv-muted" data-testid="admin-sample-notice">Includes {data.sample_count} SAMPLE demo conversations seeded for the pitch.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <div role="group" aria-label="Period" className="flex border border-hv-navy">
              {[7, 30].map((d) => (
                <button key={d} type="button" aria-pressed={days === d} onClick={() => setDays(d)} data-testid={`admin-period-${d}-button`}
                  className={`px-4 py-2 font-heading text-xs font-semibold uppercase tracking-wider ${days === d ? "bg-hv-navy text-white" : "bg-white text-hv-navy"}`}>{d} days</button>
              ))}
            </div>
            <button type="button" onClick={exportCsv} className="hv-btn-light hv-btn-sm bg-white" data-testid="admin-export-csv-button"><Download size={14} /> Export CSV</button>
            <button type="button" onClick={crawl} className="hv-btn-light hv-btn-sm bg-white" data-testid="admin-refresh-crawl-button"><RefreshCw size={14} /> Refresh prices</button>
          </div>
        </div>
        {!data ? <div className="h-64 animate-pulse bg-white" /> : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <Kpi label="Questions" value={data.total} testid="kpi-total" />
              <Kpi label="Sessions" value={data.sessions} testid="kpi-sessions" />
              <Kpi label="Answered" value={`${data.answered_rate}%`} testid="kpi-answered" />
              <Kpi label="Thumbs up / down" value={`${data.feedback.up} / ${data.feedback.down}`} testid="kpi-feedback" />
              <Kpi label="English / French" value={`${data.lang_split.en} / ${data.lang_split.fr}`} testid="kpi-lang-split" />
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              <Panel title={`Top questions (${days} days)`} testid="panel-top-questions" className="lg:col-span-2"><RankList items={data.top_questions} label="question" testid="top-questions-list" /></Panel>
              <Panel title="Most-asked tickers" testid="panel-top-tickers">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.top_tickers} layout="vertical" margin={{ left: 8, right: 8 }}>
                      <CartesianGrid horizontal={false} stroke="#DFDFDF" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#666666" }} />
                      <YAxis type="category" dataKey="ticker" width={56} tick={{ fontSize: 11, fill: "#333333", fontFamily: "Montserrat" }} />
                      <Tooltip cursor={{ fill: "#F9F9F9" }} />
                      <Bar dataKey="count" fill="#225072" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              <Panel title="Unanswered or low-confidence" testid="panel-low-confidence" className="lg:col-span-2">
                <ul className="max-h-80 divide-y divide-hv-rule overflow-y-auto text-[13px]">
                  {data.low_confidence.map((x) => (
                    <li key={x.id} className="flex justify-between gap-3 py-2"><span>{x.question}</span><span className="whitespace-nowrap text-hv-muted">{x.answered ? "low confidence" : "unanswered"} · {x.lang.toUpperCase()} · {x.created_at.slice(0, 10)}</span></li>
                  ))}
                </ul>
              </Panel>
              <Panel title="Content gaps (no good source)" testid="panel-content-gaps"><RankList items={data.content_gaps} label="question" testid="content-gaps-list" /></Panel>
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              <Panel title="Thumbs-down answers" testid="panel-thumbs-down" className="lg:col-span-2"><Transcripts items={data.thumbs_down} /></Panel>
              <Panel title={`Advisor briefs downloaded (${days} days)`} testid="panel-briefs" className="lg:col-span-2">
                <div className="mb-5 grid grid-cols-3 gap-3">
                  {[["Briefs", data.briefs.total, "briefs-total"], ["Personalized", data.briefs.personalized, "briefs-personalized"], ["Distinct answers", data.briefs.answers, "briefs-answers"]].map(([l, v, id]) => (
                    <div key={id} className="bg-hv-section p-3">
                      <p className="font-heading text-[10px] font-semibold uppercase tracking-wider text-hv-muted">{l}</p>
                      <p className="font-heading text-2xl font-bold text-hv-navy" data-testid={id}>{v}</p>
                    </div>
                  ))}
                </div>
                <p className="mb-2 font-heading text-[11px] font-semibold uppercase tracking-wider text-hv-muted">Most downloaded answers</p>
                {data.briefs.top.length ? <RankList items={data.briefs.top} label="question" testid="briefs-top-list" /> : <p className="text-[13px] text-hv-muted">No briefs downloaded yet.</p>}
              </Panel>
              <Panel title="Data freshness" testid="panel-data-status">
                {status && (
                  <dl className="space-y-2 text-[13px]">
                    <div className="flex justify-between"><dt className="text-hv-muted">Prices as at</dt><dd className="font-semibold" data-testid="status-prices-as-at">{status.prices_as_at} ({status.prices_source === "live" ? "live crawl" : "CSV fallback"})</dd></div>
                    <div className="flex justify-between"><dt className="text-hv-muted">Funds</dt><dd className="font-semibold">{status.funds}</dd></div>
                    <div className="flex justify-between"><dt className="text-hv-muted">Knowledge pages / chunks</dt><dd className="font-semibold" data-testid="status-kb">{status.kb_pages} / {status.kb_chunks}</dd></div>
                    <div className="flex justify-between"><dt className="text-hv-muted">Last full crawl</dt><dd className="font-semibold">{status.last_crawl?.finished_at?.slice(0, 16).replace("T", " ") || "in progress"}</dd></div>
                  </dl>
                )}
                <div className="mt-6 h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.daily}><XAxis dataKey="date" hide /><YAxis hide /><Tooltip /><Bar dataKey="count" fill="#183457" /></BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[11px] text-hv-muted">Daily questions</p>
              </Panel>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    if (!getToken()) { setChecked(true); return; }
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setToken(null)).finally(() => setChecked(true));
  }, []);
  const logout = useCallback(() => { http.post("/auth/logout").catch(() => {}); setToken(null); setUser(null); }, []);
  if (!checked) return null;
  return user ? <Dashboard user={user} onLogout={logout} /> : <Login onDone={setUser} />;
}

